import moment from 'moment';
import parser from 'cron-parser';
import clone from 'lodash/clone';
import uuid from 'uuid/v4';
import { stringify } from 'query-string';
import { OIDC_BASE_URL } from './consts';

const DAYS_OF_WEEK = [
    { text: 'Sun', value: 'SUN' },
    { text: 'Mon', value: 'MON' },
    { text: 'Tues', value: 'TUE' },
    { text: 'Wed', value: 'WED' },
    { text: 'Thurs', value: 'THU' },
    { text: 'Fri', value: 'FRI' },
    { text: 'Sat', value: 'SAT' }
];

/**
 * Empty initial state for Job Builder
 */
const today = moment()
    .startOf('day')
    .format('YYYY-MM-DD[T]HH:mm');

const EMPTY_WHEN = {
    days: DAYS_OF_WEEK.map(day => false),
    firstDueDate: today,
    expirationDate: today,
    repeatType: 'Daily'
};

const createCronExpression = when => {
    const { days, firstDueDate, repeatType } = when;
    const momentObj = moment(firstDueDate);
    const minute = momentObj.minutes();
    const hour = momentObj.hours();
    const date = momentObj.date();
    let weekDays = [];
    days.forEach((day, i) => {
        day && weekDays.push(i);
    }, this);

    weekDays = weekDays.join();

    switch (repeatType) {
        case 'Daily':
            return `${minute} ${hour} * * *`;
        case 'Weekly':
            return `${minute} ${hour} * * ${weekDays || '*'}`;
        case 'Monthly':
            return `${minute} ${hour} ${date} * *`;
        default:
            console.error(`Invalid repeat type. Scheduling daily.`);
            return `${minute} ${hour} * * *`;
    }
};

const getDateInstancesFromCron = (expression, startDate, endDate) => {
    try {
        endDate = endDate || startDate;
        startDate = moment(startDate).subtract(1, 'days');
        const options = { currentDate: startDate, endDate: endDate, iterator: true };
        const interval = parser.parseExpression(expression, options);
        let dateInstances = [];
        let last = { done: false };

        while (!last.done) {
            try {
                last = interval.next();
                const momentObj = last.value._date;
                dateInstances.push(momentObj.toDate());
            } catch (e) {
                break;
            }
        }

        return dateInstances;
    } catch (err) {
        console.error(`error parsing Cron Expression: '${expression}' : ${JSON.stringify(err)}`);
        return [];
    }
};

const getIntervalFromCron = cronExp => {
    const ALL = '*';
    const MAX_CRON_LENGTH = 5;
    const params = cronExp.split(' ', MAX_CRON_LENGTH);
    const weekDays = params.pop();
    const years = params.pop();
    const date = params.pop();

    if (weekDays !== ALL) {
        return 'Weekly';
    }
    if (date !== ALL) {
        return 'Monthly';
    }
    return 'Daily';
};

const getRepeatSentence = repeatState => {
    const { expirationDate, firstDueDate, repeatType } = repeatState;
    const oneTimeTask = expirationDate === firstDueDate;
    const formatDate = date => moment(date).format('MMM Do, YYYY');

    if (oneTimeTask) {
        return `One-Time Task`;
    } else {
        return `Repeats ${repeatType || ''} ${formatDate(firstDueDate)} - ${formatDate(
            expirationDate
        )}`;
    }
};

// pluralizes intervals for UI
const pluralize = key => {
    const plurals = {
        Daily: 'days',
        Weekly: 'weeks',
        Monthly: 'months'
    };
    return plurals[key];
};

const setDatetime = (value: any, oldValue: string, mode: string) => {
    const validDate = mode !== 'date' || value > new Date();
    let oldMoment = moment(oldValue);
    let newMoment = moment(value);

    if (mode === 'date') {
        newMoment.hour(oldMoment.hour()).minute(oldMoment.minute());
    } else {
        newMoment.year(oldMoment.year()).dayOfYear(oldMoment.dayOfYear());
    }

    return validDate ? newMoment.format() : oldValue;
};

const redirectToLogin = () => {
    window.location =
        `${OIDC_BASE_URL}/connect/authorize?` +
        stringify({
            authority: `${OIDC_BASE_URL}`,
            client_id: 'analytics',
            scope: `openid profile analytics analytics-api email offline_access`,
            response_mode: 'form_post',
            response_type: 'code id_token token',
            redirect_uri: `${window.location.origin}/api/v1/loginToken`,
            state: b64EncodeUnicode(window.location.href),
            nonce: uuid()
        });
};

const b64EncodeUnicode = str => {
    return btoa(
        encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function(match, p1) {
            return String.fromCharCode(parseInt(p1, 16));
        })
    );
};

const b64DecodeUnicode = str => {
    return decodeURIComponent(
        Array.prototype.map
            .call(atob(str), function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join('')
    );
};

export {
    createCronExpression,
    DAYS_OF_WEEK,
    EMPTY_WHEN,
    getDateInstancesFromCron,
    getIntervalFromCron,
    getRepeatSentence,
    pluralize,
    setDatetime,
    redirectToLogin,
    b64EncodeUnicode,
    b64DecodeUnicode
};



// WEBPACK FOOTER //
// ./modules/utils.js